/**
 * Error-classification predicates used by both the legacy `TRPCErrorBoundary`
 * wrapper and the new layered handlers (`QueryCache.onError`,
 * `QueryErrorBoundary`, route `errorComponent`). Extracted from the wrapper so
 * the rules are testable and shared across owners.
 *
 * See docs/guidelines/trpc-error-handling.md.
 */

import {
    isSyntheticSessionExpiredError,
    isTrpcExecutionDeadlineError,
    isUnauthorizedTrpcError,
} from '@client/trpc/sessionHandling';
import { isAuthRoute } from '@client/utils/common/auth';
import { isNetworkError } from '@client/utils/errors/network-error-detection';
import { CONTACT_WRITE_DENIAL_MESSAGES } from '@shared/constants';

import { DEFAULT_QUERY_ERROR_SURFACE, type ErrorSurface, SURFACES_WITH_DOWNSTREAM_SENTRY_OWNER } from './query-meta';

// Re-exports so call sites don't import deep into other modules for the
// canonical predicates.
export { isNetworkError, isSyntheticSessionExpiredError, isUnauthorizedTrpcError };

/**
 * Minimal shape of the auth store the predicates need. Accepting an interface
 * (rather than calling `useAuthStore.getState()` here) keeps these pure and
 * testable without the Zustand singleton.
 */
export interface AuthSessionSnapshot {
    /** `authTokenExpired` flag from the auth store. */
    readonly authTokenExpired: boolean;
    /** Live token expiry check (e.g. `useAuthStore.getState().isTokenExpired`). */
    readonly isTokenExpired: () => boolean;
}

function hasMessage(error: unknown, message: string): boolean {
    return typeof error === 'object' && error !== null && 'message' in error && error.message === message;
}

const EXPECTED_CONTACT_WRITE_DENIAL_MESSAGES: ReadonlySet<string> = new Set(
    Object.values(CONTACT_WRITE_DENIAL_MESSAGES),
);

function isExpectedContactWriteDenial(error: unknown): boolean {
    if (typeof error !== 'object' || error === null) {
        return false;
    }

    const trpcError = error as { data?: { code?: unknown }; message?: unknown };
    return (
        trpcError.data?.code === 'FORBIDDEN' &&
        typeof trpcError.message === 'string' &&
        EXPECTED_CONTACT_WRITE_DENIAL_MESSAGES.has(trpcError.message)
    );
}

/**
 * True when the error is the well-known "session expired" signal we expect on
 * auth routes after a token times out, or the synthetic one produced by
 * `authTokenCheckLink`. These are normal user-inactivity events — they should
 * not pop a modal and should not be captured to Sentry.
 */
export function isExpectedSessionExpiration(error: unknown, session: AuthSessionSnapshot): boolean {
    if (isSyntheticSessionExpiredError(error) && session.authTokenExpired) {
        return true;
    }

    if (!isUnauthorizedTrpcError(error)) {
        return false;
    }

    return hasMessage(error, 'Session expired') && session.authTokenExpired && isAuthRoute();
}

/** tRPC error codes for which re-issuing the identical request can never succeed. */
const NON_RETRYABLE_TRPC_CODES: ReadonlySet<string> = new Set(['FORBIDDEN', 'UNAUTHORIZED', 'NOT_FOUND']);

/**
 * True unless the error carries a tRPC code that denotes a stable denial
 * (authorisation / existence), where an identical retry is guaranteed to fail
 * the same way. `InlineQueryError` uses this to suppress its "Try again"
 * affordance in one place instead of at every call site.
 */
export function isRetryableQueryError(error: unknown): boolean {
    if (typeof error !== 'object' || error === null) {
        return true;
    }

    const trpcError = error as { data?: { code?: unknown } };
    return !(typeof trpcError.data?.code === 'string' && NON_RETRYABLE_TRPC_CODES.has(trpcError.data.code));
}

/**
 * True when a failed query must replace its content instead of leaving cached
 * data visible. Retryable refresh failures may continue to show cached data,
 * while stable denials always block it.
 */
export function hasBlockingQueryError<T>(error: unknown, data: T | null | undefined): boolean {
    return error != null && (data == null || !isRetryableQueryError(error));
}

/**
 * Decide whether the cache layer should capture this error to Sentry.
 *
 * Returns `false` when:
 * - the error is a known/expected session expiration (handled by RequireAuth);
 * - the error is an expected contact create/update business denial;
 * - the error is a network failure (the user is likely offline — captured
 *   would be noise);
 * - the declared `errorSurface` has a downstream Sentry owner (`route`,
 *   `boundary`), so capturing in the cache would double-fire.
 */
export function shouldCaptureToSentry(
    error: unknown,
    surface: ErrorSurface = DEFAULT_QUERY_ERROR_SURFACE,
    session: AuthSessionSnapshot,
): boolean {
    if (SURFACES_WITH_DOWNSTREAM_SENTRY_OWNER.has(surface)) {
        return false;
    }

    if (isExpectedSessionExpiration(error, session)) {
        return false;
    }

    if (isExpectedContactWriteDenial(error)) {
        return false;
    }

    // The server captures one correlated event for the entire expired HTTP batch.
    if (isTrpcExecutionDeadlineError(error)) return false;

    if (isNetworkError(error)) {
        return false;
    }

    return true;
}
